import { z } from "zod";
import { addressSchema } from "../../../api/schemas";
import { ELYSIUM } from "../../../config/elysium";

export const xTaskKinds = ["follow", "retweet", "like", "comment", "quote"] as const;
export const xUsernameSchema = z.string().regex(/^[a-zA-Z0-9_]{1,15}$/);
const tweetId = z.string().regex(/^[1-9][0-9]*$/);
const timestamp = z.number().int().nonnegative().safe();
export function isXPostUrl(value: string): boolean {
  if (value.length > 300 || value !== value.trim()) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && ["x.com", "www.x.com", "twitter.com", "www.twitter.com"].includes(url.hostname) &&
      !url.username && !url.password && !url.port &&
      /^\/(?:[a-zA-Z0-9_]{1,15}|i\/web)\/status\/[1-9][0-9]*\/?$/.test(url.pathname);
  } catch { return false; }
}
export const xPostUrlSchema = z.string().max(300).refine(isXPostUrl, "Use an HTTPS X or Twitter status URL (maximum 300 characters).");
const availableKinds = z.array(z.enum(xTaskKinds)).max(xTaskKinds.length).refine(kinds => new Set(kinds).size === kinds.length);
const optionalTweetId = tweetId.or(z.literal("")).nullish();
export const xCatalogSchema = z.discriminatedUnion("enabled", [
  // No disabled-catalog fixture is supplied: only its documented discriminator is required.
  z.object({ enabled: z.literal(false) }),
  // OAuth availability does not require every task target to be configured.
  z.object({ enabled: z.literal(true), oauthEnabled: z.boolean(), tasks: availableKinds,
    followUsername: xUsernameSchema.or(z.literal("")).nullish(), tweetId: optionalTweetId,
    commentTweetId: optionalTweetId, quoteTweetId: optionalTweetId, likeUrl: xPostUrlSchema.or(z.literal("")).nullish() }),
]);
export const xConnectionSchema = z.discriminatedUnion("linked", [
  z.object({ wallet: addressSchema, linked: z.literal(false), xUserId: z.null(), xUsername: z.null() }),
  z.object({ wallet: addressSchema, linked: z.literal(true), xUserId: tweetId, xUsername: xUsernameSchema }),
]);
export const xOAuthStartSchema = z.object({ authorizationUrl: z.string().url().refine(value => {
  try {
    const url = new URL(value);
    return value === value.trim() && url.protocol === "https:" && url.hostname === "api.x.com" &&
      !url.username && !url.password && !url.port && !value.includes("#") && url.pathname === "/oauth/authorize" &&
      url.searchParams.getAll("oauth_token").length === 1 && !!url.searchParams.get("oauth_token")?.trim();
  } catch { return false; }
}, "Unexpected OAuth provider URL") });
export const xLikeSchema = z.object({ passed: z.boolean(), url: xPostUrlSchema });
const resultFields = { passed: z.boolean(), reason: z.string().min(1).nullable(), targetTweetId: tweetId.or(z.literal("")) };
export const xCheckSchema = z.object({ xUsername: xUsernameSchema,
  task: z.object({ kind: z.enum(xTaskKinds), ...resultFields })
    .refine(task => task.kind === "follow" || task.targetTweetId !== "", "Post tasks require a tweet target"),
});
export const xProgressSchema = z.object({ tasks: z.array(z.object({ walletAddress: addressSchema,
  xUsername: xUsernameSchema, taskKind: z.enum(xTaskKinds), targetUsername: xUsernameSchema.nullable(),
  ...resultFields, firstPassedAt: timestamp.nullable(), checkedAt: timestamp })
  .refine(task => task.taskKind === "follow" || task.targetTweetId !== "", "Post tasks require a tweet target")) });
export const xMintAuthorizationSchema = z.object({ xUsername: xUsernameSchema, contractAddress: addressSchema,
  chainId: z.number().int().positive().safe(), mintFeeWei: z.literal("0"), valueWei: z.literal("0"), tierUsd: z.literal("0"),
  rewardBps: z.object({ min: z.literal(5), max: z.literal(10) }), taskMintCap: z.union([z.literal(2222), z.literal(1666)]), deadline: timestamp.max(8_640_000_000_000),
  call: z.object({ to: addressSchema, valueWei: z.literal("0"), data: z.string().regex(/^0x(?:[0-9a-fA-F]{2}){4,}$/) }),
}).refine(value => value.call.to.toLowerCase() === value.contractAddress.toLowerCase(), "Task mint target mismatch")
  // Older Elysium envelopes may report 2222; cap is not signed. Fresh on-chain
  // preflight still requires the current 1666 cap and enforces remaining supply.
  .refine(value => value.chainId === ELYSIUM.chainId || value.taskMintCap === 2222, "Task mint cap/deployment mismatch");
export type XCatalog = z.infer<typeof xCatalogSchema>;
export type XConnection = z.infer<typeof xConnectionSchema>;
export type XProgressRow = z.infer<typeof xProgressSchema>["tasks"][number];
export type XTaskKind = typeof xTaskKinds[number];
export type XMintAuthorization = z.infer<typeof xMintAuthorizationSchema>;
